import type { Entry, PostEntryInput } from '@taler/api-client'
import { addMoney, type CurrencyCode, type Money, zeroMoney } from '@taler/domain'
import type { Component } from 'vue'
import { type FlowKind, resolveTarget, type RowDraft, rowCurrency } from './draft.ts'
import type { Target } from './targets.ts'

/** A row's core fields an extension may keep; any it does not list is not sent, so the grid disables it. */
export type CoreField = 'payee' | 'memo' | 'tags' | 'splits'

/** Everything an extension may read about a row, resolved once per change. */
export interface RowView {
  readonly kind: FlowKind
  readonly row: RowDraft
  readonly account: Target | undefined
  /** One per split, in split order; undefined where no target is chosen yet. */
  readonly counters: readonly (Target | undefined)[]
  readonly currency: CurrencyCode | undefined
  /** The splits' summed magnitude, once every split has an amount and the row has a currency. */
  readonly total: Money | undefined
}

export interface ExtensionMatch {
  /** A draft survives while the key stays the same; a new key starts a fresh draft. */
  readonly key: string
  /** Names what the row became, e.g. "Loan payment · Home renovation loan". */
  readonly label: string
}

export interface ExtensionProblem {
  /** `blocked`: no detail can fix the row, only a different account can. */
  readonly severity: 'needs-details' | 'blocked'
  readonly message: string
}

export type SubmitShape<M, D> =
  /** One ordinary entry in the atomic batch, built by the extension instead of the grid. */
  | { readonly shape: 'postings'; build(match: M, draft: D, view: RowView): PostEntryInput }
  /** Not an entry: a module endpoint builds its own. Resolves to the draft to show once posted. */
  | { readonly shape: 'command'; run(match: M, draft: D, view: RowView): Promise<D> }
  /** The grid's own entry posts in the batch; the module record then references it. */
  | { readonly shape: 'attach'; run(posted: Entry, match: M, draft: D, view: RowView): Promise<void> }

/** Detail components receive `match`, `draft` and `view` props and emit `update:draft`. */
export interface EntryExtension<M extends ExtensionMatch, D> {
  readonly id: string
  detect(view: RowView): M | undefined
  readonly carries: ReadonlySet<CoreField>
  initialDraft(match: M, view: RowView): D
  problems(match: M, draft: D, view: RowView): readonly ExtensionProblem[]
  /** Accounts whose postings this module gives meaning to; a system one is offered only when claimed. */
  owns?(target: Target): boolean
  /** A word beside a target in the account picker, e.g. "Loan payment". */
  optionHint?(target: Target): string | undefined
  readonly detail?: Component
  /** `panel` renders the detail beside the grid instead of under the row. */
  readonly placement: 'inline' | 'panel'
  readonly submit: SubmitShape<M, D>
}

declare const erased: unique symbol
type Opaque = { readonly [erased]: true }

/** An extension with its match and draft types erased, so differently-typed ones share one list. */
export type RegisteredExtension = EntryExtension<ExtensionMatch & Opaque, Opaque>

export function defineEntryExtension<M extends ExtensionMatch, D>(
  extension: EntryExtension<M, D>,
): RegisteredExtension {
  return extension as unknown as RegisteredExtension
}

export function rowView(
  kind: FlowKind,
  row: RowDraft,
  targetsById: ReadonlyMap<string, Target>,
  foreignTargets: readonly Target[] = [],
): RowView {
  const currency = rowCurrency(row, targetsById, foreignTargets)
  const amounts = row.splits.map((split) => split.amount)
  const total =
    currency === undefined || amounts.some((amount) => amount === null)
      ? undefined
      : amounts.reduce<Money>((sum, amount) => addMoney(sum, amount!), zeroMoney(currency))
  return {
    kind,
    row,
    account: resolveTarget(row.accountId, row.accountLedgerId, targetsById, foreignTargets),
    counters: row.splits.map((split) =>
      resolveTarget(split.targetId, split.targetLedgerId, targetsById, foreignTargets),
    ),
    currency,
    total,
  }
}

/** The first extension in list order that recognises the row; order is priority. */
export function matchExtension(
  extensions: readonly RegisteredExtension[],
  view: RowView,
): { readonly extension: RegisteredExtension; readonly match: ExtensionMatch & Opaque } | undefined {
  for (const extension of extensions) {
    const match = extension.detect(view)
    if (match !== undefined) return { extension, match }
  }
  return undefined
}
